/**
 * SubmissionDetailDialog — view all fields of a single submission.
 * Auto-mark-as-read happens in the parent on open.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Mail, Reply, ChevronRight } from 'lucide-react';

export function SubmissionDetailDialog({ open, submission, onClose, onConvert, onQuickReply }) {
  if (!submission) return null;
  const fields = submission.fields || {};
  const submittedAt = submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#FFFEF5]" data-testid="submission-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#B89B5E]" /> Submission Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-stone-600 flex flex-wrap gap-x-4 gap-y-1">
            <span><strong>Submitted:</strong> {submittedAt}</span>
            <span><strong>Deck:</strong> {submission.deck_title || '—'}</span>
            {submission.recipient_ip && <span><strong>IP:</strong> {submission.recipient_ip}</span>}
            {submission.converted && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Converted to opportunity
              </span>
            )}
          </div>

          <div className="rounded-lg border border-stone-200 overflow-hidden bg-white">
            <table className="w-full text-sm">
              <tbody>
                {Object.keys(fields).length === 0 && (
                  <tr><td className="px-4 py-3 italic text-stone-400" colSpan={2}>(no fields)</td></tr>
                )}
                {Object.entries(fields).map(([k, v]) => (
                  <tr key={k} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 text-xs uppercase tracking-widest text-stone-500 align-top w-1/3">{k}</td>
                    <td className="px-4 py-2.5 text-stone-800 align-top whitespace-pre-wrap">{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onQuickReply} data-testid="detail-reply-btn">
              <Reply className="w-4 h-4 mr-1" /> Quick reply
            </Button>
            {submission.converted && submission.opportunity_id ? (
              <a href={`/opportunity/${submission.opportunity_id}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                  Open opportunity <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
            ) : (
              <Button onClick={onConvert} className="bg-[#B89B5E] hover:bg-[#A88A4D] text-white" data-testid="detail-convert-btn">
                Convert to Opportunity <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
